import React, { useMemo } from 'react';
import { Card } from '../../CompositeComponents/Card/Card';
import type { PieChartProps } from './types';

// ─── PieChart ─────────────────────────────────────────────────────────────────

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];
const R = 70;
const INNER_R = 40; // used when donut=true
const CX = 90;
const CY = 90;

const DEFAULT_FORMATTER = (v: number): string => v.toLocaleString('en-IN');

function slicePath(cx: number, cy: number, r: number, innerR: number, startAngle: number, endAngle: number, donut: boolean): string {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const x1 = cx + r * Math.cos(toRad(startAngle));
  const y1 = cy + r * Math.sin(toRad(startAngle));
  const x2 = cx + r * Math.cos(toRad(endAngle));
  const y2 = cy + r * Math.sin(toRad(endAngle));
  const large = endAngle - startAngle > 180 ? 1 : 0;

  if (donut) {
    const ix1 = cx + innerR * Math.cos(toRad(startAngle));
    const iy1 = cy + innerR * Math.sin(toRad(startAngle));
    const ix2 = cx + innerR * Math.cos(toRad(endAngle));
    const iy2 = cy + innerR * Math.sin(toRad(endAngle));
    return `M${x1} ${y1} A${r} ${r} 0 ${large} 1 ${x2} ${y2} L${ix2} ${iy2} A${innerR} ${innerR} 0 ${large} 0 ${ix1} ${iy1} Z`;
  }
  return `M${cx} ${cy} L${x1} ${y1} A${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

export function PieChart({ data, title, donut = false, valueFormatter = DEFAULT_FORMATTER, className }: PieChartProps) {
  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);

  const slices = useMemo(() => {
    let angle = -90;
    return data.map((d, i) => {
      const sweep = total > 0 ? (d.value / total) * 360 : 0;
      const start = angle;
      angle += sweep;
      return { ...d, start, end: angle, color: d.color ?? PALETTE[i % PALETTE.length] };
    });
  }, [data, total]);

  return (
    <Card className={className}>
      {title && (
        <p className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">{title}</p>
      )}

      <div className="flex items-center gap-4 flex-wrap">
        <svg
          viewBox="0 0 180 180"
          className="w-36 h-36 flex-shrink-0"
          role="img"
          aria-label={title ?? 'Pie chart'}
        >
          {slices.map((s, i) => (
            <path
              key={i}
              d={slicePath(CX, CY, R, INNER_R, s.start, s.end, donut)}
              fill={s.color}
              stroke="white"
              strokeWidth="1"
            >
              <title>{`${s.label}: ${valueFormatter(s.value)} (${total > 0 ? ((s.value / total) * 100).toFixed(1) : 0}%)`}</title>
            </path>
          ))}
          {donut && (
            <circle cx={CX} cy={CY} r={INNER_R - 2} fill="white" className="dark:fill-neutral-800" />
          )}
        </svg>

        {/* Legend */}
        <ul className="flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-300 min-w-0">
          {slices.map((s, i) => (
            <li key={i} className="flex items-center gap-2 truncate">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
              <span className="truncate">{s.label}</span>
              <span className="ml-auto pl-2 font-medium flex-shrink-0">
                {total > 0 ? ((s.value / total) * 100).toFixed(0) : 0}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
